import { API, HOST } from '@/utils/http'

/**
 * Create a new chat session
 * @param {number} organizationId - The organization ID
 * @param {string} name - Name of the chat session
 * @param {Object} llmConfig - Optional LLM configuration
 * @returns {Promise<Object>} The created chat session
 */
export async function createChatSession(organizationId, name, llmConfig = null) {
  try {
    const payload = {
      name: name
    }

    if (llmConfig) {
      payload.llm_config = llmConfig
    }

    const response = await API.post(`${HOST}/organizations/${organizationId}/chats`, payload)
    return response.data
  } catch (error) {
    console.error('Error creating chat session:', error)
    throw new Error(error.response?.data?.detail || 'Failed to create chat session')
  }
}

/**
 * Get all chat sessions for an organization
 * @param {number} organizationId - The organization ID
 * @returns {Promise<Object>} List of chat sessions
 */
export async function getChatSessions(organizationId) {
  try {
    const response = await API.get(`${HOST}/organizations/${organizationId}/chats`)
    return response.data
  } catch (error) {
    console.error('Error fetching chat sessions:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch chat sessions')
  }
}

/**
 * Get a single chat session by UUID
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @returns {Promise<Object>} The chat session data
 */
export async function getChatSession(orgId, sessionUuid) {
  try {
    const response = await API.get(`${HOST}/organizations/${orgId}/chats/${sessionUuid}`)
    return response.data
  } catch (error) {
    console.error('Error fetching chat session:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch chat session')
  }
}

/**
 * Update a chat session
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @param {Object} updates - Object containing fields to update (e.g., { name: 'New Name' })
 * @returns {Promise<Object>} The updated chat session
 */
export async function updateChatSession(orgId, sessionUuid, updates) {
  try {
    const response = await API.patch(`${HOST}/organizations/${orgId}/chats/${sessionUuid}`, updates)
    return response.data
  } catch (error) {
    console.error('Error updating chat session:', error)
    throw new Error(error.response?.data?.detail || 'Failed to update chat session')
  }
}

/**
 * Delete a chat session (soft delete)
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @returns {Promise<void>}
 */
export async function deleteChatSession(orgId, sessionUuid) {
  try {
    await API.delete(`${HOST}/organizations/${orgId}/chats/${sessionUuid}`)
  } catch (error) {
    console.error('Error deleting chat session:', error)
    throw new Error(error.response?.data?.detail || 'Failed to delete chat session')
  }
}

/**
 * Create a new message in a chat session
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @param {string} content - The message content
 * @param {string|null} reportUuid - Optional report UUID for editing an existing report
 * @param {number|null} reportVersion - Optional report version being edited (defaults to latest)
 * @returns {Promise<Object>} The created message
 */
export async function createMessage(orgId, sessionUuid, content, reportUuid = null, reportVersion = null) {
  try {
    const payload = { content }
    if (reportUuid) {
      payload.report_uuid = reportUuid
    }
    if (reportVersion !== null) {
      payload.report_version = reportVersion
    }

    const response = await API.post(
      `${HOST}/organizations/${orgId}/chats/${sessionUuid}/messages`,
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error creating message:', error)
    throw new Error(error.response?.data?.detail || 'Failed to create message')
  }
}

/**
 * Get available LLM models with metadata
 * @param {number} orgId - The organization ID
 * @returns {Promise<Object>} List of available models with labels and descriptions
 */
export async function getAvailableModels(orgId) {
  try {
    const response = await API.get(`${HOST}/organizations/${orgId}/chats/available-models`)
    return response.data
  } catch (error) {
    console.error('Error fetching available models:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch available models')
  }
}

/**
 * Get LLM configuration for a chat session
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @returns {Promise<Object>} The LLM configuration
 */
export async function getLLMConfig(orgId, sessionUuid) {
  try {
    const response = await API.get(`${HOST}/organizations/${orgId}/chats/${sessionUuid}/llm-config`)
    return response.data
  } catch (error) {
    console.error('Error fetching LLM config:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch LLM config')
  }
}

// ==================== Prompt Management API ====================

/**
 * Get all prompts for a chat session with entity details
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @returns {Promise<Object>} Session prompts categorized by type
 */
export async function getSessionPrompts(orgId, sessionUuid) {
  try {
    const response = await API.get(`${HOST}/organizations/${orgId}/chats/${sessionUuid}/prompts`)
    return response.data
  } catch (error) {
    console.error('Error fetching session prompts:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch session prompts')
  }
}

/**
 * Link an entity (project or layer) prompt to a chat session
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @param {string} entityType - Type of entity ('project' or 'layer')
 * @param {number} entityId - The entity ID to link
 * @returns {Promise<Object>} Response with prompt_id
 */
export async function linkEntityPrompt(orgId, sessionUuid, entityType, entityId) {
  try {
    const response = await API.post(
      `${HOST}/organizations/${orgId}/chats/${sessionUuid}/prompts/${entityType}/${entityId}`
    )
    return response.data
  } catch (error) {
    console.error(`Error linking ${entityType} prompt:`, error)
    throw new Error(error.response?.data?.detail || `Failed to link ${entityType} prompt`)
  }
}

/**
 * Unlink a prompt from a chat session
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @param {number} promptId - The prompt ID to unlink
 * @returns {Promise<void>}
 */
export async function unlinkPrompt(orgId, sessionUuid, promptId) {
  try {
    await API.delete(`${HOST}/organizations/${orgId}/chats/${sessionUuid}/prompts/${promptId}`)
  } catch (error) {
    console.error('Error unlinking prompt:', error)
    throw new Error(error.response?.data?.detail || 'Failed to unlink prompt')
  }
}

/**
 * Update prompt content (affects all sessions using this prompt)
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @param {number} promptId - The prompt ID to update
 * @param {string} content - New prompt content
 * @returns {Promise<Object>} Response with updated_at timestamp
 */
export async function updatePromptContent(orgId, sessionUuid, promptId, content) {
  try {
    const response = await API.put(
      `${HOST}/organizations/${orgId}/chats/${sessionUuid}/prompts/${promptId}`,
      { content }
    )
    return response.data
  } catch (error) {
    console.error('Error updating prompt content:', error)
    throw new Error(error.response?.data?.detail || 'Failed to update prompt content')
  }
}

// ==================== Report Generation API ====================

/**
 * Get all reports linked to a chat session
 * @param {number} orgId - The organization ID
 * @param {string} sessionUuid - The chat session UUID
 * @returns {Promise<Object>} List of reports linked to the session
 */
export async function getSessionReports(orgId, sessionUuid) {
  try {
    const response = await API.get(
      `${HOST}/organizations/${orgId}/sessions/${sessionUuid}/reports`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching session reports:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch session reports')
  }
}

